// src/features/task/TaskAssigneeControl.tsx
// Assignee control for the task detail rail: assign, reassign, or clear the
// assignee. Wraps AssignTask / UnassignTask.

import { useState } from 'react';
import { observer } from '@react';
import { useTask, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardContent,
  Alert,
} from '@react/components';
import { UserPicker } from '@/components/UserPicker';
import { getDisplayName } from '@/features/task/logic';
import { errMsg } from '@/features/task/panelHelpers';

interface TaskAssigneeControlProps {
  taskId: string;
  assignee?: any;
  editable?: boolean;
  onChanged?: () => void;
}

export const TaskAssigneeControl = observer(({
  taskId,
  assignee,
  editable = true,
  onChanged,
}: TaskAssigneeControlProps) => {
  const store = useTask();
  const { t } = useI18n();

  const [formOpen, setFormOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const assign = async (userId: string, displayName: string) => {
    const id = userId.trim();
    if (!id) return;
    setBusy(true);
    setError(null);
    try {
      await store.AssignTask(taskId, {
        assignee: { entity: { id }, display_name: displayName || undefined },
      } as any);
      setFormOpen(false);
      onChanged?.();
    } catch (e) {
      setError(errMsg(e, t('task.assignControl.assignError')));
    } finally {
      setBusy(false);
    }
  };

  const unassign = async () => {
    setBusy(true);
    setError(null);
    try {
      await store.UnassignTask(taskId);
      onChanged?.();
    } catch (e) {
      setError(errMsg(e, t('task.assignControl.unassignError')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
          {t('task.field.assignee')}
        </p>

        {error && <Alert variant="error">{error}</Alert>}

        {assignee ? (
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium text-primary shrink-0">
                {getDisplayName(assignee).charAt(0).toUpperCase()}
              </div>
              <span className="text-sm font-medium text-foreground truncate">{getDisplayName(assignee)}</span>
            </div>
            {editable && (
              <Button size="sm" variant="ghost" className="text-destructive shrink-0" disabled={busy} onClick={unassign}>
                {t('task.assignControl.clear')}
              </Button>
            )}
          </div>
        ) : (
          <span className="text-sm text-muted-foreground">{t('task.unassigned')}</span>
        )}

        {editable && !formOpen && (
          <Button size="sm" variant="secondary" onClick={() => setFormOpen(true)}>
            {assignee ? t('task.assignControl.reassign') : t('task.assignControl.assign')}
          </Button>
        )}

        {formOpen && (
          <div className="space-y-2 rounded-md border border-border p-2">
            <UserPicker
              value=""
              onChange={assign}
              placeholder={t('task.field.assigneePlaceholder')}
            />
            {busy && <p className="text-xs text-muted-foreground">{t('common.loading')}</p>}
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => setFormOpen(false)}>
              {t('common.cancel')}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
});

export default TaskAssigneeControl;
